import React, { useState } from 'react';
import { LivePage, FilterBar, DataTable, Age, Code, assertExecOk, clustersAPI, useNamespaces, NamespaceSelect } from './LivePage';
import WorkloadDetailDrawer from '../components/WorkloadDetailDrawer';
import { useCluster } from '../context/ClusterContext';
import { useAuth } from '../context/AuthContext';
import ActionsMenu, { MENU_ITEM } from '../components/ActionsMenu';

const lbl = (text) => <label style={{ fontSize:12, fontWeight:600, color:'var(--ink-2)', display:'block', marginBottom:4 }}>{text}</label>;

function CreateConfigMapModal({ clusterId, initialNamespace = 'default', namespaces = [], onClose, onCreated }) {
  const [form, setForm] = useState({ name:'', namespace: initialNamespace });
  const [pairs, setPairs] = useState([{ key:'', value:'' }]);
  const [files, setFiles] = useState([]); // [{ key, content }] — one per uploaded file
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  const updatePair = (i, field, val) => setPairs(p => p.map((x,idx) => idx===i ? {...x,[field]:val} : x));
  const addPair    = () => setPairs(p => [...p, {key:'', value:''}]);
  const removePair = (i) => setPairs(p => p.filter((_,idx)=>idx!==i));
  const removeFile = (i) => setFiles(f => f.filter((_,idx)=>idx!==i));

  // Mirrors `kubectl create configmap foo --from-file=myfile.conf`: the file's name
  // becomes the data key, its full text content becomes the value — for config files
  // (nginx.conf, settings.json, etc.) that don't fit a single key=value row.
  const handleFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    picked.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const key = file.name.replace(/[^A-Za-z0-9._-]/g, '_');
        setFiles(f => [...f, { key, content: String(reader.result) }]);
      };
      reader.readAsText(file);
    });
    e.target.value = ''; // allow re-selecting the same file name
  };

  const handleCreate = async () => {
    if (!form.name) { setError('Name is required'); return; }
    const validPairs = pairs.filter(p => p.key.trim());
    if (validPairs.length === 0 && files.length === 0) {
      setError('At least one key-value pair or uploaded file is required'); return;
    }
    setSaving(true); setError('');
    try {
      const pairLines = validPairs.map(p => `  ${p.key}: "${p.value.replace(/"/g,'\\"')}"`);
      // YAML literal block scalar (|) for file contents — safe for quotes, newlines,
      // and special characters that the quoted-string form above can't handle.
      // 4-space indent nests the content as the block's body, re-indenting every
      // line of the file itself so it stays inside that block.
      const fileLines = files.map(f => `  ${f.key}: |\n${f.content.split('\n').map(l => `    ${l}`).join('\n')}`);
      const dataLines = [...pairLines, ...fileLines].join('\n');
      const yaml = `apiVersion: v1
kind: ConfigMap
metadata:
  name: ${form.name}
  namespace: ${form.namespace}
data:
${dataLines}`;
      // The whole YAML is embedded inside a single-quoted shell string below — any
      // literal "'" in it (common in uploaded config files, e.g. a comment or a
      // string literal) would otherwise close that quote early and spill the rest
      // of the file into the shell as bogus commands. Standard POSIX escape: close
      // the quote, insert an escaped literal quote, reopen the quote.
      const shellSafeYaml = yaml.replace(/'/g, `'\\''`);
      const cmd = `kubectl create namespace ${form.namespace} --dry-run=client -o yaml | kubectl apply -f - 2>/dev/null; echo '${shellSafeYaml}' | kubectl apply -f -`;
      const r = await clustersAPI.exec(clusterId, cmd, { section: 'configuration', action: 'create' });
      const out = r.data?.output || r.data?.stdout || '';
      const err = r.data?.stderr || '';
      const combined = `${out}\n${err}`.trim();
      const ok = /(created|configured|unchanged)/.test(combined) && !/error|denied|forbidden/i.test(err);
      if (ok) { onCreated(); onClose(); }
      else setError(combined || 'Create failed (no output from cluster — is the cluster agent connected?)');
    } catch (e) { setError(e.response?.data?.detail || 'Create failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="card" style={{ width:560, maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:700 }}>Create ConfigMap</h2>
          <button onClick={onClose} className="btn btn-ghost btn-icon" style={{ fontSize:20 }}>✕</button>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>{lbl('Name *')}<input placeholder="my-config" value={form.name} onChange={e=>setF('name',e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'-'))} className="input" /></div>
            <div>{lbl('Namespace')}<NamespaceSelect namespaces={namespaces} ns={form.namespace} onChange={v=>setF('namespace',v)} /></div>
          </div>
          <div>
            {lbl('Key-Value Data')}
            <div style={{ border:'1px solid var(--border)', borderRadius:7, overflow:'hidden' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 32px', background:'var(--surface-2)', padding:'6px 10px', fontSize:11, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
                <span>Key</span><span>Value</span><span></span>
              </div>
              {pairs.map((pair, i) => (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 32px', gap:4, padding:'6px 10px', borderTop:'1px solid var(--border)', alignItems:'center' }}>
                  <input placeholder="KEY_NAME" value={pair.key}
                    onChange={e=>updatePair(i,'key',e.target.value)}
                    className="input" style={{ fontFamily:'monospace', fontSize:12 }} />
                  <input placeholder="value" value={pair.value}
                    onChange={e=>updatePair(i,'value',e.target.value)}
                    className="input" style={{ fontSize:12 }} />
                  <button onClick={()=>removePair(i)} disabled={pairs.length===1}
                    className="btn btn-ghost btn-icon btn-sm" style={{ color:'var(--color-danger-500)', fontSize:16 }}>×</button>
                </div>
              ))}
              <div style={{ padding:'8px 10px', borderTop:'1px solid var(--border)' }}>
                <button onClick={addPair} className="btn btn-link">
                  + Add key
                </button>
              </div>
            </div>
          </div>
          <div>
            {lbl('Data From File (optional)')}
            <div style={{ fontSize:11.5, color:'var(--muted)', marginBottom:6 }}>
              Upload one or more config files (e.g. nginx.conf, settings.json) — each becomes
              its own data key, named after the file, with the file's full contents as the value.
            </div>
            <input type="file" multiple onChange={handleFilePick} className="input" style={{ fontSize:12 }} />
            {files.length > 0 && (
              <div style={{ border:'1px solid var(--border)', borderRadius:7, overflow:'hidden', marginTop:8 }}>
                {files.map((f, i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    padding:'6px 10px', borderTop: i===0 ? 'none' : '1px solid var(--border)' }}>
                    <span style={{ fontFamily:'monospace', fontSize:12 }}>{f.key}</span>
                    <span style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ fontSize:11, color:'var(--muted)' }}>{f.content.length} chars</span>
                      <button onClick={()=>removeFile(i)}
                        className="btn btn-ghost btn-icon btn-sm" style={{ color:'var(--color-danger-500)', fontSize:16 }}>×</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {error && <div className="banner danger">{error}</div>}
          <div style={{ display:'flex', gap:8, marginTop:4 }}>
            <button onClick={handleCreate} disabled={saving} className="btn btn-primary" style={{ flex:1 }}>
              {saving ? '⟳ Creating...' : '+ Create ConfigMap'}
            </button>
            <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ConfigMaps() {
  const { hasSectionAction } = useAuth();
  const { selectedCluster } = useCluster();
  const canCreate = hasSectionAction('configuration', 'create');
  const canDelete = hasSectionAction('configuration', 'delete');
  // Server-side namespace scoping: only this namespace's configmaps are fetched (fast),
  // instead of all 1280 across the fleet. Defaults to 'default'.
  const { namespaces, ns, setNs } = useNamespaces(selectedCluster?.id);
  const [nameFilter, setNameFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  // Describe + view/edit YAML for one ConfigMap, in the shared drawer.
  const [detailFor, setDetailFor] = useState(null);

  return (
    <>
    {/* fetchFn depends on `ns` — changing the namespace recreates it, and LivePage's
        useEffect([load]) re-runs the fetch scoped to the new namespace. Guard on ns so the
        first render (before the ns list loads) doesn't fire an all-namespaces query. */}
    <LivePage title="ConfigMaps" deps={[ns]} waitFor={ns} fetchFn={id => clustersAPI.getWorkloadPaged('configmaps', id, { pageSize: 500, namespace: ns || undefined })}>
      {(data, refresh, clusterId) => {
        // API returns a raw K8s List (items mixing ConfigMap+Secret); normalize
        // ConfigMap items into the flat shape the table expects.
        const _raw = data.configmaps || data.items || [];
        const items = _raw
          .filter(it => (it.kind || 'ConfigMap') === 'ConfigMap')
          .map(it => it.metadata ? {
            name: it.metadata.name,
            namespace: it.metadata.namespace,
            data_keys: Object.keys(it.data || {}).concat(Object.keys(it.binaryData || {})).join(', '),
            created_at: it.metadata.creationTimestamp,
          } : it);
        const filtered = items.filter(c =>
          c.name.toLowerCase().includes(nameFilter.toLowerCase())
        );

        const handleDelete = async (name, namespace) => {
          if (!window.confirm(`Delete configmap "${name}" in "${namespace}"?`)) return;
          try {
            const r = await clustersAPI.exec(clusterId, `kubectl delete configmap ${name} -n ${namespace} --ignore-not-found=true`, { section: 'configuration', action: 'delete' });
            assertExecOk(r, 'Delete');   // kubectl failures come back HTTP 200 on stderr
            refresh();
          } catch (e) { alert(`Delete failed: ${e.response?.data?.detail || e.message}`); }
        };

        return <>
          <FilterBar count={filtered.length} total={items.length} label="configmaps">
            <NamespaceSelect namespaces={namespaces} ns={ns} onChange={setNs} />
            <input placeholder="Filter by name..." value={nameFilter} onChange={e => setNameFilter(e.target.value)} />
            {canCreate && (
            <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm" style={{ marginLeft:'auto' }}>
              + Create ConfigMap
            </button>
            )}
          </FilterBar>
          <DataTable cols={['Name', 'Namespace', 'Data Keys', 'Age', '']}
            rows={filtered.map(c => (
              <tr key={`${c.namespace}/${c.name}`}>
                <td><Code>{c.name}</Code></td>
                <td style={{ color: 'var(--ink-2)', fontSize: 13 }}>{c.namespace}</td>
                <td style={{ fontSize:12, color:'var(--ink-2)' }}>{c.data_keys || '—'}</td>
                <td><Age ts={c.created_at} /></td>
                <td>
                  <ActionsMenu>
                    <button onClick={() => setDetailFor({ namespace: c.namespace, name: c.name })}
                      style={MENU_ITEM} title="View data and editable YAML">
                      Details
                    </button>
                    {canDelete && (
                    <button onClick={() => handleDelete(c.name, c.namespace)} style={{ ...MENU_ITEM, color: 'var(--color-danger-500)' }}>
                      Delete
                    </button>
                    )}
                  </ActionsMenu>
                </td>
              </tr>
            ))} />
          {showCreate && (
            <CreateConfigMapModal
              clusterId={clusterId}
              initialNamespace={ns || 'default'}
              namespaces={namespaces}
              onClose={() => setShowCreate(false)}
              onCreated={() => { setShowCreate(false); refresh(); }}
            />
          )}
        </>;
      }}
    </LivePage>
    {detailFor && selectedCluster && (
      <WorkloadDetailDrawer
        clusterId={selectedCluster.id}
        kind="ConfigMap"
        resource="configmaps"
        namespace={detailFor.namespace}
        name={detailFor.name}
        onClose={() => setDetailFor(null)}
      />
    )}
    </>
  );
}
